import { useEffect, useState } from 'react';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { EntityName } from 'record-it-time-common/EntityName';
import { Utils } from 'record-it-utils/utils/Utils/Utils';
import { UserRepository } from '../../../classes/entities/User/UserRepository';
import { User } from '../../../classes/entities/User/userTypes';
import { UserGroupRepository } from '../../../classes/entities/UserGroup/UserGroupRepository';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { UserGroupToUserRepository } from '../../../classes/entities/UserGroupToUser/UserGroupToUserRepository';
import { AuthenticationData } from '../../../context/AuthenticationContext';
import { useEntityChangedListener } from '../../entity/useEntityChangedListener/useEntityChangedListener';
import { useEntityRepository } from '../../entity/useEntityRepository';

export function useCurrentUserData(
  authenticationData: AuthenticationData | null
): CurrentUserData | null {
  const userRepository = useEntityRepository(EntityName.User);
  const userGroupRepository = useEntityRepository(EntityName.UserGroup);
  const userGroupToUserRepository = useEntityRepository(
    EntityName.UserGroupToUser
  );

  const [currentUserData, setCurrentUserData] =
    useState<CurrentUserData | null>(() => {
      return generateCurrentUserData({
        userRepository,
        userGroupRepository,
        userGroupToUserRepository,
        authenticationData
      });
    });

  const updateCurrentUserData = useEventCallback(
    (authData: AuthenticationData | null) => {
      const newData = generateCurrentUserData({
        userRepository,
        userGroupRepository,
        userGroupToUserRepository,
        authenticationData: authData
      });
      setCurrentUserData((oldData) => {
        if (Utils.isDeepEqual(newData, oldData)) {
          return oldData;
        }

        return newData;
      });
    }
  );

  useEffect(() => {
    updateCurrentUserData(authenticationData);
  }, [updateCurrentUserData, authenticationData]);

  useEntityChangedListener(EntityName.User, () => {
    updateCurrentUserData(authenticationData);
  });

  useEntityChangedListener(EntityName.UserGroup, () => {
    updateCurrentUserData(authenticationData);
  });

  useEntityChangedListener(EntityName.UserGroupToUser, () => {
    updateCurrentUserData(authenticationData);
  });

  return currentUserData;
}

function generateCurrentUserData({
  userRepository,
  userGroupRepository,
  userGroupToUserRepository,
  authenticationData
}: GenerateCurrentUserDataOptions): CurrentUserData | null {
  const user = authenticationData
    ? userRepository.getById(authenticationData.userId)
    : null;
  if (!user) {
    return null;
  }

  const adminUserGroups: Array<UserGroup> = [];
  const userGroups: Array<UserGroup> = [];

  const userRelations = userGroupToUserRepository.getByUserId(user.id);
  for (const relation of userRelations) {
    const userGroup = userGroupRepository.getById(relation.ownerUserGroupId);
    if (!userGroup) {
      continue;
    }

    userGroups.push(userGroup);
    if (relation.groupAdmin) {
      adminUserGroups.push(userGroup);
    }
  }

  return {
    user,
    adminUserGroups,
    userGroups
  };
}

export type CurrentUserData = {
  user: User;
  userGroups: Array<UserGroup>;
  adminUserGroups: Array<UserGroup>;
};

type GenerateCurrentUserDataOptions = {
  userRepository: UserRepository;
  userGroupRepository: UserGroupRepository;
  userGroupToUserRepository: UserGroupToUserRepository;
  authenticationData: AuthenticationData | null;
};
